import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Euro, GraduationCap, Package, Receipt, ShoppingBag, Store } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import TurnoverPeriods from "@/components/admin/TurnoverPeriods";

export const PAYMENT_LABELS = {
  paid: "Payée",
  pending: "En attente",
  failed: "Échouée",
  expired: "Expirée",
  refunded: "Remboursée",
  awaiting_cheque: "Chèque attendu",
  cancelled: "Annulée",
};

export const PAYMENT_STYLES = {
  paid: "bg-sage-light text-sage",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-terra/15 text-terra",
  expired: "bg-stone-200 text-stone-mute",
  refunded: "bg-terra/15 text-terra",
  awaiting_cheque: "bg-amber-100 text-amber-800",
  cancelled: "bg-stone-200 text-stone-mute",
};

export function OrderDate({ value }) {
  if (!value) return null;
  return <>{new Date(value).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>;
}

function TurnoverPanel({ t }) {
  return (
    <div data-testid="stat-revenue" className="grid gap-4 rounded-3xl border border-amber-800/15 bg-white p-6 shadow-sm lg:grid-cols-[1.1fr_1fr_1.3fr]">
      <div className="flex flex-col justify-center rounded-2xl bg-amber-deep p-6 text-white">
        <Euro size={22} className="opacity-80" />
        <p data-testid="turnover-total" className="mt-3 font-serif text-4xl font-semibold">{formatPrice(t.total_cents)}</p>
        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-white/80">Chiffre d'affaires total depuis l'ouverture</p>
        <p className="mt-2 text-xs text-white/70">Site + écoles, commissions reversées déduites</p>
      </div>
      <div className="rounded-2xl border border-amber-800/10 bg-cream p-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone-mute"><Store size={15} className="text-honey" /> CA du site</p>
        <p data-testid="turnover-site" className="mt-3 font-serif text-3xl font-semibold text-bark">{formatPrice(t.site_cents)}</p>
        <p className="mt-1 text-xs text-bark-soft">Commandes boutique payées (livraison incluse)</p>
      </div>
      <div className="rounded-2xl border border-amber-800/10 bg-cream p-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-stone-mute"><GraduationCap size={15} className="text-honey" /> CA des écoles</p>
        <p data-testid="turnover-school-net" className="mt-3 font-serif text-3xl font-semibold text-bark">{formatPrice(t.school_net_cents)}</p>
        <ul className="mt-3 space-y-1 text-sm">
          <li className="flex justify-between gap-3 text-bark-soft"><span>Ventes écoles ({t.school_paid_orders} payées/remises)</span><span data-testid="turnover-school-gross" className="font-medium text-bark">{formatPrice(t.school_gross_cents)}</span></li>
          <li className="flex justify-between gap-3 text-bark-soft"><span>Commission reversée</span><span data-testid="turnover-school-commission" className="font-medium text-terra">−{formatPrice(t.school_commission_cents)}</span></li>
        </ul>
        {t.school_pending_orders > 0 && (
          <p data-testid="turnover-school-pending" className="mt-3 rounded-lg bg-amber-100 px-3 py-1.5 text-xs text-amber-800">{t.school_pending_orders} commande(s) en attente de paiement · {formatPrice(t.school_pending_cents)} (non comptées)</p>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (!stats) {
    return <p data-testid="dashboard-loading" className="py-20 text-center font-serif text-xl text-bark-soft">Chargement du tableau de bord…</p>;
  }

  const cards = [
    { icon: Receipt, label: "Commandes payées", value: stats.paid_orders, testid: "stat-orders" },
    { icon: ShoppingBag, label: "À préparer", value: stats.pending_fulfillment, testid: "stat-pending" },
    { icon: Package, label: "Produits en ligne", value: stats.products_count, testid: "stat-products" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <h1 className="font-serif text-3xl font-semibold text-bark">Tableau de bord</h1>

      <TurnoverPanel t={stats.turnover} />
      <TurnoverPeriods />

      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <c.icon size={22} className="text-honey" />
            <p className="mt-3 font-serif text-3xl font-semibold text-bark">{c.value}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.15em] text-stone-mute">{c.label}</p>
          </div>
        ))}
      </div>

      {stats.pending_quotes > 0 && (
        <div data-testid="quote-alert-panel" className="rounded-2xl border border-amber-700/40 bg-amber-100/70 p-6">
          <p className="flex items-center gap-2 font-serif text-lg font-semibold text-amber-900">
            {stats.pending_quotes} commande(s) « cadeau hors France » à devis
          </p>
          <p className="mt-1 text-sm text-bark-soft">
            Ces abonnements-cadeaux sont livrés hors de France : proposez un devis de frais de port avant de valider.{" "}
            <Link to="/admin/commandes" data-testid="quote-alert-link" className="font-medium text-amber-deep hover:text-bark">Voir les commandes</Link>
          </p>
        </div>
      )}

      {stats.low_stock.length > 0 && (
        <div data-testid="low-stock-panel" className="rounded-2xl border border-amber-700/30 bg-amber-50 p-6">
          <p className="flex items-center gap-2 font-medium text-amber-900">
            <AlertTriangle size={18} /> Stock faible — pensez à réapprovisionner
          </p>
          <ul className="mt-3 space-y-1 text-sm text-bark-soft">
            {stats.low_stock.map((p) => (
              <li key={p.product_id} data-testid={`low-stock-${p.product_id}`}>
                <Link to={`/admin/produits/${p.product_id}`} className="font-medium text-bark underline-offset-2 hover:underline">{p.name}</Link>
                {" — "}
                <span className={p.stock === 0 ? "font-semibold text-terra" : "text-amber-800"}>
                  {p.stock === 0 ? "épuisé" : `${p.stock} restant(s)`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-bark">Dernières commandes</h2>
          <Link to="/admin/commandes" data-testid="dashboard-all-orders" className="text-sm font-medium text-amber-deep hover:text-bark">
            Tout voir
          </Link>
        </div>
        {stats.recent_orders.length === 0 ? (
          <p className="text-sm text-bark-soft">Aucune commande pour le moment.</p>
        ) : (
          <ul className="divide-y divide-amber-900/10">
            {stats.recent_orders.map((o) => (
              <li key={o.session_id} data-testid={`recent-order-${o.session_id}`} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <span className="text-stone-mute"><OrderDate value={o.created_at} /></span>
                <span className="flex-1 truncate px-3 text-bark">
                  {o.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ") || "—"}
                </span>
                <span className="font-semibold text-bark">{formatPrice(Math.round((o.amount || 0) * 100))}</span>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${PAYMENT_STYLES[o.payment_status] || "bg-sand text-bark-soft"}`}>
                  {PAYMENT_LABELS[o.payment_status] || o.payment_status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
